import React, { useState, useEffect, useRef } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/race-tracker/components/ui/alert-dialog";
import { Button } from "@/race-tracker/components/ui/button";

/**
 * Controlled destructive confirmation (Radix AlertDialog).
 * Uses a plain Button for confirm so we can await async onConfirm before closing.
 */
export default function DeleteConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = "Delete",
  cancelText = "Cancel",
  onConfirm,
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) setIsDeleting(false);
  }, [open]);

  const handleOpenChange = (next) => {
    if (isDeleting) return;
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
      if (mounted.current) onOpenChange(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="z-[100]">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description != null && description !== "" ? (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>{cancelText}</AlertDialogCancel>
          <Button type="button" variant="destructive" size="sm" disabled={isDeleting} onClick={handleConfirm}>
            {isDeleting ? "Deleting…" : confirmText}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
